import type { CaptionMode, MemeTemplate } from "./types";

const apiOrigin = import.meta.env.VITE_API_ORIGIN?.replace(/\/$/, "") ?? "";

const demoTemplates: MemeTemplate[] = [
  {
    id: "demo-focused",
    label: "Focused reaction",
    imageUrl:
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 900 650'><rect width='900' height='650' fill='%23e7ece5'/><circle cx='430' cy='205' r='92' fill='%23c88f67'/><path d='M250 555c36-150 286-152 342 0z' fill='%232c3230'/><rect x='198' y='390' width='504' height='184' rx='18' fill='%231a1a1a'/><rect x='232' y='422' width='436' height='108' rx='10' fill='%23f6f7f2'/><path d='M356 200c44 28 105 27 150-1' stroke='%23211b18' stroke-width='18' stroke-linecap='round' fill='none'/><circle cx='396' cy='190' r='10'/><circle cx='486' cy='190' r='10'/></svg>",
    rawImageUrl:
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 900 650'><rect width='900' height='650' fill='%23e7ece5'/><circle cx='430' cy='205' r='92' fill='%23c88f67'/><path d='M250 555c36-150 286-152 342 0z' fill='%232c3230'/><rect x='198' y='390' width='504' height='184' rx='18' fill='%231a1a1a'/><rect x='232' y='422' width='436' height='108' rx='10' fill='%23f6f7f2'/><path d='M356 200c44 28 105 27 150-1' stroke='%23211b18' stroke-width='18' stroke-linecap='round' fill='none'/><circle cx='396' cy='190' r='10'/><circle cx='486' cy='190' r='10'/></svg>",
    status: "approved",
    classifierModel: "demo",
    classifierReason: "Demo approved template.",
    captions: ["me reading production logs", "ship it after one more test"]
  }
];

export async function fetchTemplates(query: string): Promise<MemeTemplate[]> {
  const params = new URLSearchParams();
  if (query.trim()) params.set("q", query.trim());
  const response = await fetch(`${apiOrigin}/api/templates?${params.toString()}`);
  if (!response.ok || !isJsonResponse(response)) return demoTemplates;
  const data = (await response.json()) as { templates: MemeTemplate[] };
  return data.templates.map(withAbsoluteImageUrls);
}

export async function fetchTemplate(id: string): Promise<MemeTemplate> {
  const response = await fetch(`${apiOrigin}/api/templates/${id}`);
  if (!response.ok || !isJsonResponse(response)) {
    const demoTemplate = demoTemplates.find((template) => template.id === id);
    if (demoTemplate) return demoTemplate;
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Template not found.");
  }
  const data = (await response.json()) as { template: MemeTemplate };
  return withAbsoluteImageUrls(data.template);
}

export async function uploadTemplate(form: FormData): Promise<MemeTemplate> {
  const response = await fetch(`${apiOrigin}/api/templates`, {
    method: "POST",
    body: form
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Upload failed.");
  }
  const data = (await response.json()) as { template: MemeTemplate };
  return withAbsoluteImageUrls(data.template);
}

export async function saveCaption(templateId: string, text: string, mode: CaptionMode): Promise<void> {
  if (!text.trim()) return;
  const response = await fetch(`${apiOrigin}/api/templates/${templateId}/captions`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text: text.trim(), mode })
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Caption save failed.");
  }
}

function withAbsoluteImageUrls(template: MemeTemplate): MemeTemplate {
  return {
    ...template,
    imageUrl: absoluteUrl(template.imageUrl),
    rawImageUrl: absoluteUrl(template.rawImageUrl ?? template.imageUrl)
  };
}

function isJsonResponse(response: Response): boolean {
  return response.headers.get("content-type")?.includes("application/json") ?? false;
}

function absoluteUrl(value: string): string {
  if (!apiOrigin || /^https?:\/\//.test(value) || value.startsWith("data:")) return value;
  return `${apiOrigin}${value.startsWith("/") ? "" : "/"}${value}`;
}
